import React from 'react';
import { useData } from '../contexts/DataContext';
import {
  calculateDistanceToFinish,
  findNearestRouteIndex
} from '../helpers/gpxHelper';

import {
  Grid,
  Typography,
  LinearProgress,
  Box,
  Divider,
  Paper
} from '@mui/material';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RouteIcon from '@mui/icons-material/Route';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export default function Timeline() {
  const { gpxPoints: routePoints, latest, stats, checkpoints } = useData();
  if (!latest || !stats || !routePoints?.length || !checkpoints?.length) return null;

  const totalDistance = routePoints.reduce((sum, pt, i, arr) =>
    i === 0 ? 0 : sum + arr[i - 1].distanceTo(pt), 0);

  const distanceToEnd = calculateDistanceToFinish(routePoints, latest);
  const percentComplete = totalDistance > 0 ? 1 - (distanceToEnd * 1000) / totalDistance : null;

  const etaMin = stats.avgSpeed10 > 0 ? (distanceToEnd / stats.avgSpeed10) * 60 : null;
  const etaStr = etaMin ? formatEta(etaMin) : '—';
  const percent = percentComplete != null ? Math.round(percentComplete * 100) : null;
  const currentIndex = findNearestRouteIndex(routePoints, latest);

  const segmentDistance = (fromIdx, toIdx) => {
    let d = 0;
    for (let i = fromIdx; i < toIdx; i++) d += routePoints[i].distanceTo(routePoints[i + 1]);
    return d;
  };

  const distanceAlongRouteTo = (toIdx) => {
    let d = 0;
    for (let i = 0; i < toIdx && i < routePoints.length - 1; i++) {
      d += routePoints[i].distanceTo(routePoints[i + 1]);
    }
    return d;
  };

  const allMarkers = checkpoints.map(cp => {
    const cpIndex = findNearestRouteIndex(routePoints, cp);
    const isUpcoming = cpIndex > currentIndex;
    const distM = isUpcoming ? segmentDistance(currentIndex, cpIndex) : 0;
    const etaMinHere = isUpcoming && stats.avgSpeed10 > 0
      ? (distM / 1000) / stats.avgSpeed10 * 60
      : null;

    return {
      title: cp.title,
      positionPercent: (distanceAlongRouteTo(cpIndex) / totalDistance) * 100,
      isUpcoming,
      etaMinutes: etaMinHere,
      etaStr: etaMinHere ? formatEta(etaMinHere) : null,
      etaAbsolute: etaMinHere
        ? new Date(latest.timestamp * 1000 + etaMinHere * 60000)
        : null,
      distance: distM / 1000
    };
  });

  // Add finish marker
  if (routePoints.length - 1 > currentIndex) {
    const distM = segmentDistance(currentIndex, routePoints.length - 1);
    const etaFinish = stats.avgSpeed10 > 0 ? (distM / 1000) / stats.avgSpeed10 * 60 : null;
    allMarkers.push({
      title: 'Finish 🏁',
      positionPercent: 100,
      isUpcoming: true,
      etaMinutes: etaFinish,
      etaStr: etaFinish ? formatEta(etaFinish) : null,
      etaAbsolute: etaFinish
        ? new Date(latest.timestamp * 1000 + etaFinish * 60000)
        : null,
      distance: distM / 1000
    });
  }

  const upcomingCheckpoints = allMarkers
    .filter(cp => cp.isUpcoming && cp.etaStr)
    .sort((a, b) => a.etaMinutes - b.etaMinutes);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <Typography variant="body2" color="text.secondary">Laatste meting</Typography>
          <Typography variant="subtitle1">
            {new Date(latest.timestamp * 1000).toLocaleTimeString()}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="body2" color="text.secondary">Afstand tot finish</Typography>
          <Typography variant="subtitle1">{distanceToEnd.toFixed(1)} km</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="body2" color="text.secondary">Tijd tot finish</Typography>
          <Typography variant="subtitle1">{etaStr}</Typography>
        </Grid>
        {percent != null && (
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Voortgang</Typography>
            <Typography variant="subtitle1">{percent}%</Typography>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 4, mb: 6, position: 'relative', height: 120 }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{ height: 12, borderRadius: 6, position: 'absolute', bottom: 0, left: 0, right: 0 }}
        />

        {allMarkers.map((cp, idx) => (
          <Box
            key={`${cp.title}-${idx}`}
            sx={{
              position: 'absolute',
              left: `${cp.positionPercent}%`,
              transform: 'translateX(-50%)',
              bottom: 0,
              textAlign: 'center',
              zIndex: 3,
              width: 'max-content',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                backgroundColor: 'white',
                px: 0.5,
                py: 0.2,
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                whiteSpace: 'nowrap',
                position: 'relative',
                bottom: 20 + (idx % 2 === 0 ? 0 : -16),
                mb: 0.5,
                color: cp.isUpcoming ? 'inherit' : '#999'
              }}
            >
              {cp.title}
            </Typography>
            <Box sx={{
              height: 48,
              width: 2,
              backgroundColor: cp.isUpcoming ? 'black' : '#ccc',
              margin: '0 auto'
            }} />
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: cp.isUpcoming ? '#1976d2' : '#ccc',
              border: '2px solid white',
              boxShadow: cp.isUpcoming ? '0 0 0 1px rgba(25,118,210,0.4)' : 'none',
              margin: '0 auto',
              mt: 0.5
            }} />
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Opkomende controlepunten
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 2,
          alignItems: 'stretch',
          overflow: 'visible',
        }}
      >
        {upcomingCheckpoints.map((cp, idx) => (
          <Paper
            key={idx}
            elevation={1}
            sx={{
              p: 1.5,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              borderRadius: 2,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2">
                <LocationOnIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                <strong>{cp.title}</strong>
              </Typography>

              <Typography variant="body2" color="text.secondary">
                <ScheduleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {cp.etaAbsolute?.toLocaleTimeString('nl-NL', {
                  hour: '2-digit',
                  minute: '2-digit'
                })} ({formatEta(cp.etaMinutes)})
              </Typography>

              <Typography variant="body2" color="text.secondary">
                <RouteIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {cp.distance.toFixed(1)} km
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <ArrowForwardIcon sx={{ color: '#bbb', fontSize: 20 }} />
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

function formatEta(min) {
  const hours = Math.floor(min / 60);
  const minutes = Math.round(min % 60);
  return hours > 0 ? `${hours}u ${minutes}m` : `${minutes}m`;
}
